// admin-web/src/pages/clubs/ClubMembersPage.tsx
import {
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    orderBy,
    query,
    updateDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { auth, db, functions } from "../../firebase";
import { useAuth } from "../../auth/AuthProvider";
import { ClubMemberPicker } from "./ClubMemberPicker";

type Club = {
  name: string;
  category?: string;
  isActive?: boolean;
  threadId?: string | null;
  messagingMode?: "broadcast" | "group";
  broadcastPublisherUid?: string;
};

type MemberRow = {
  uid: string;
  emailLower?: string;
  role: "member" | "leader" | "owner";
  status: "active" | "removed" | "invited";
  memberType?: string;
};

type InviteRow = {
  id: string;
  emailLower: string;
  role: "member" | "leader";
  status: "invited" | "accepted" | "expired" | "revoked";
  createdAt?: any;
  expiresAt?: any;
};

function normEmail(s: string) {
  return String(s || "").trim().toLowerCase();
}

export function ClubMembersPage() {
  const { hasPermission } = useAuth();
  const { clubId } = useParams<{ clubId: string }>();
  const [club, setClub] = useState<Club | null>(null);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const [members, setMembers] = useState<MemberRow[]>([]);
  const [invites, setInvites] = useState<InviteRow[]>([]);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"member" | "leader">("member");
  const [savingInvite, setSavingInvite] = useState(false);

  const [filter, setFilter] = useState("");

  const filteredMembers = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return members;
    return members.filter((m) => `${m.emailLower || ""} ${m.uid} ${m.role} ${m.status}`.toLowerCase().includes(f));
  }, [members, filter]);

  const filteredInvites = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return invites;
    return invites.filter((i) => `${i.emailLower} ${i.role} ${i.status}`.toLowerCase().includes(f));
  }, [invites, filter]);

  async function load() {
    if (!clubId) return;
    setLoading(true);
    setErr(null);
    setOk(null);
    try {
      const clubRef = doc(db, "clubs", clubId);
      const clubSnap = await getDoc(clubRef);
      if (!clubSnap.exists()) throw new Error("Club not found.");
      setClub(clubSnap.data() as any);

      const memSnap = await getDocs(query(collection(db, "clubs", clubId, "members")));
      const memRows: MemberRow[] = memSnap.docs.map((d) => {
        const data = d.data() as any;
        return {
          uid: d.id,
          emailLower: data.emailLower,
          role: data.role || "member",
          status: data.status || "active",
          memberType: data.memberType,
        };
      });

      memRows.sort((a, b) => {
        const rank = (r: MemberRow) => (r.role === "owner" ? 0 : r.role === "leader" ? 1 : 2);
        const ra = rank(a), rb = rank(b);
        if (ra !== rb) return ra - rb;
        return (a.emailLower || a.uid).localeCompare(b.emailLower || b.uid);
      });

      setMembers(memRows);

      const invSnap = await getDocs(
        query(collection(db, "clubs", clubId, "invites"), orderBy("createdAt", "desc"))
      );
      const invRows: InviteRow[] = invSnap.docs.map((d) => {
        const data = d.data() as any;
        return {
          id: d.id,
          emailLower: data.emailLower,
          role: data.role || "member",
          status: data.status || "invited",
          createdAt: data.createdAt,
          expiresAt: data.expiresAt,
        };
      });

      setInvites(invRows);
    } catch (e: any) {
      setErr(e?.message || "Failed to load.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  async function createInvite() {
    if (!clubId) return;
    setErr(null);
    setOk(null);

    const email = normEmail(inviteEmail);
    if (!email || !email.includes("@")) {
      setErr("Enter a valid email.");
      return;
    }

    setSavingInvite(true);
    try {
      const fn = httpsCallable(functions, "createClubInvite");
      const res = await fn({ clubId, email, role: inviteRole });
      const data: any = res.data || {};
      setOk(data.reused ? "Invite already existed (still active)." : "Invite created.");
      setInviteEmail("");
      setInviteRole("member");
      await load();
    } catch (e: any) {
      setErr(e?.message || "Failed to create invite.");
    } finally {
      setSavingInvite(false);
    }
  }

  async function revokeInvite(inviteId: string) {
    if (!clubId) return;
    setErr(null);
    setOk(null);
    try {
      await updateDoc(doc(db, "clubs", clubId, "invites", inviteId), { status: "revoked" });
      setOk("Invite revoked.");
      await load();
    } catch (e: any) {
      setErr(e?.message || "Failed to revoke invite.");
    }
  }

  async function deleteInvite(inviteId: string) {
    if (!clubId) return;
    setErr(null);
    setOk(null);
    try {
      await deleteDoc(doc(db, "clubs", clubId, "invites", inviteId));
      setOk("Invite deleted.");
      await load();
    } catch (e: any) {
      setErr(e?.message || "Failed to delete invite.");
    }
  }

  async function setRole(uid: string, role: "member" | "leader") {
    if (!clubId) return;
    setErr(null);
    setOk(null);
    try {
      const fn = httpsCallable(functions, "setClubMemberRole");
      await fn({ clubId, memberUid: uid, role });
      setOk("Role updated.");
      await load();
    } catch (e: any) {
      setErr(e?.message || "Failed to update role.");
    }
  }

  async function removeMember(uid: string) {
    if (!clubId) return;
    setErr(null);
    setOk(null);
    try {
      const fn = httpsCallable(functions, "removeClubMember");
      await fn({ clubId, memberUid: uid });
      setOk("Member removed.");
      await load();
    } catch (e: any) {
      setErr(e?.message || "Failed to remove member.");
    }
  }

  return (
    <div className="card">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <div>
          <h2 style={{ marginTop: 0 }}>Club Members</h2>
          <p className="muted" style={{ marginTop: 6 }}>
            {club ? (
              <>
                <b>{club.name}</b>
                {club.category ? ` • ${club.category}` : ""}{" "}
                {club.isActive === false ? <span className="pill off" style={{ marginLeft: 8 }}>Inactive</span> : null}
              </>
            ) : (
              "…"
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Link to="/clubs" className="btn secondary">Back to Clubs</Link>
        </div>
      </div>

      {err ? <div className="error">{err}</div> : null}
      {ok ? <div className="success">{ok}</div> : null}
      {club?.messagingMode === "broadcast" ? <p className="muted">{club.broadcastPublisherUid
        ? "Broadcast-only club: only the designated publisher can send announcements. Other leaders can manage membership. Members cannot reply."
        : "Broadcast-only club: each leader publishes in their own Announcements from thread in Messages. Members cannot reply."}</p> : null}

      <div className="grid2">
        <div>
          <label className="label">Filter</label>
          <input className="input" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="email, uid, role…" />
        </div>
        <div>
          <label className="label">Signed in as</label>
          <div className="muted" style={{ paddingTop: 10 }}>
            {auth.currentUser?.email || auth.currentUser?.uid || "unknown"}
          </div>
        </div>
      </div>

      <hr className="hr" />

      {clubId && club?.isActive !== false && hasPermission("clubs", "manage") ? <>
        <ClubMemberPicker key={clubId} clubId={clubId} onAdded={load} />
        <hr className="hr" />
      </> : null}

      <h3>Invite by email (including first leader)</h3>
      <div className="grid2">
        <div>
          <label className="label">Email</label>
          <input className="input" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="leader@example.com" />
        </div>
        <div>
          <label className="label">Role</label>
          <select className="input" value={inviteRole} onChange={(e) => setInviteRole(e.target.value as any)}>
            <option value="member">Member</option>
            <option value="leader">Leader</option>
          </select>
        </div>
      </div>

      <button className="btn" onClick={createInvite} disabled={savingInvite} style={{ marginTop: 10 }}>
        {savingInvite ? "Creating…" : "Create Invite"}
      </button>

      <hr className="hr" />

      <h3>Members</h3>
      {loading ? (
        <div className="muted">Loading…</div>
      ) : filteredMembers.length === 0 ? (
        <div className="muted">No members found.</div>
      ) : (
        <div className="table">
          <div className="thead" style={{ gridTemplateColumns: "1fr 140px 140px 240px" }}>
            <div>User</div>
            <div>Status</div>
            <div>Role</div>
            <div></div>
          </div>

          {filteredMembers.map((m) => (
            <div className="trow" key={m.uid} style={{ gridTemplateColumns: "1fr 140px 140px 240px" }}>
              <div>
                <div style={{ fontWeight: 800 }}>{m.emailLower || m.uid}</div>
                <div className="muted" style={{ fontSize: 12 }}>{m.uid}</div>
              </div>
              <div>
                <span className={m.status === "active" ? "pill ok" : "pill off"}>{m.status}</span>
              </div>
              <div>
                <span className={m.role === "owner" ? "pill ok" : "pill"}>{m.role}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                {m.role !== "owner" ? (
                  <>
                    <button className="btn small secondary" onClick={() => setRole(m.uid, m.role === "leader" ? "member" : "leader")}>
                      {m.role === "leader" ? "Demote" : "Promote"}
                    </button>
                    <button className="btn small danger" onClick={() => removeMember(m.uid)}>
                      Remove
                    </button>
                  </>
                ) : (
                  <div className="muted" style={{ fontSize: 12, paddingTop: 8 }}>Owner (admin-only)</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <hr className="hr" />

      <h3>Invites</h3>
      {loading ? (
        <div className="muted">Loading…</div>
      ) : filteredInvites.length === 0 ? (
        <div className="muted">No invites found.</div>
      ) : (
        <div className="table">
          <div className="thead" style={{ gridTemplateColumns: "1fr 120px 140px 240px" }}>
            <div>Email</div>
            <div>Role</div>
            <div>Status</div>
            <div></div>
          </div>

          {filteredInvites.map((i) => (
            <div className="trow" key={i.id} style={{ gridTemplateColumns: "1fr 120px 140px 240px" }}>
              <div>
                <div style={{ fontWeight: 800 }}>{i.emailLower}</div>
                <div className="muted" style={{ fontSize: 12 }}>{i.id}</div>
              </div>
              <div><span className="pill">{i.role}</span></div>
              <div><span className={i.status === "invited" ? "pill ok" : "pill off"}>{i.status}</span></div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                {i.status === "invited" ? (
                  <>
                    <button className="btn small secondary" onClick={() => revokeInvite(i.id)}>Revoke</button>
                    <button className="btn small danger" onClick={() => deleteInvite(i.id)}>Delete</button>
                  </>
                ) : (
                  <button className="btn small secondary" onClick={() => deleteInvite(i.id)}>Delete</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
